import { useState, useCallback } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { ShoppingCart, Copy, Check } from 'lucide-react';

export function DashboardPage() {
  const { appUser } = useAuth();
  const [copied, setCopied] = useState(false);
  const complaintUrl =
    typeof window !== 'undefined' && appUser?.companyId
      ? `${window.location.origin}/complaint/${appUser.companyId}`
      : '';

  const copyComplaintLink = useCallback(async () => {
    if (!complaintUrl) return;
    try {
      await navigator.clipboard.writeText(complaintUrl);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 2000);
    } catch {
      setCopied(false);
    }
  }, [complaintUrl]);

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-semibold">Panel de control</h1>
        <p className="text-muted-foreground">
          Bienvenido, {appUser?.displayName || appUser?.email}.
        </p>
      </div>
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
        {appUser?.companyId && (
          <Card className="md:col-span-2 border-primary/15 bg-primary/[0.03]">
            <CardHeader>
              <CardTitle className="text-base">Buzón de Denuncias NOM-035 (Link Público)</CardTitle>
              <CardDescription>
                Comparte este enlace con tu personal para recibir reportes anónimos. No requiere inicio de sesión.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-3">
              <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
                <code className="flex-1 truncate rounded-md border bg-background px-3 py-2 text-xs sm:text-sm">
                  {complaintUrl}
                </code>
                <Button type="button" variant="outline" size="sm" className="shrink-0 gap-2" onClick={copyComplaintLink}>
                  {copied ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
                  {copied ? 'Copiado' : 'Copiar enlace'}
                </Button>
              </div>
              <a href="/app/complaints" className="text-sm text-primary hover:underline inline-block">
                Ver denuncias en panel →
              </a>
            </CardContent>
          </Card>
        )}
        <Card>
          <CardHeader>
            <CardTitle className="text-base">Procesos</CardTitle>
            <CardDescription>Gestiona tus procesos de selección</CardDescription>
          </CardHeader>
          <CardContent>
            <a href="/app/processes" className="text-sm text-primary hover:underline">
              Ir a procesos →
            </a>
          </CardContent>
        </Card>
        <Card>
          <CardHeader>
            <CardTitle className="text-base">Resultados</CardTitle>
            <CardDescription>Consulta resultados y genera reportes</CardDescription>
          </CardHeader>
          <CardContent>
            <a href="/app/results" className="text-sm text-primary hover:underline">
              Ir a resultados →
            </a>
          </CardContent>
        </Card>
        <Card>
          <CardHeader>
            <CardTitle className="text-base">Desarrollo Organizacional</CardTitle>
            <CardDescription>Directorio, bitácora, NOM-035 y clima laboral</CardDescription>
          </CardHeader>
          <CardContent>
            <a href="/app/nom035" className="text-sm text-primary hover:underline">
              Ir a desarrollo organizacional →
            </a>
          </CardContent>
        </Card>
        <Card>
          <CardHeader>
            <CardTitle className="text-base flex items-center gap-2">
              <ShoppingCart className="h-4 w-4" />
              Facturación
            </CardTitle>
            <CardDescription>Planes, facturas y pagos</CardDescription>
          </CardHeader>
          <CardContent>
            <a href="/app/billing" className="text-sm text-primary hover:underline">
              Ir a facturación →
            </a>
          </CardContent>
        </Card>
        <Card>
          <CardHeader>
            <CardTitle className="text-base">Ajustes</CardTitle>
            <CardDescription>Branding y datos de empresa</CardDescription>
          </CardHeader>
          <CardContent>
            <a href="/app/settings" className="text-sm text-primary hover:underline">
              Ir a ajustes →
            </a>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
